import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useLang } from "../lib/i18n";

const EMPTY = { total: 0, messages: 0, requests: 0, matches: 0 };
const UnreadContext = createContext({ unread: EMPTY, refresh: () => {} });

export function UnreadProvider({ children }) {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(EMPTY);
  const lastSeen = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/messages/unread");
      setUnread(data);
      const l = data.latest;
      if (l && lastSeen.current !== null && l.at > lastSeen.current && pathRef.current !== `/mensajes/${l.conversation_id}`) {
        const msg = l.type === "match" ? t("contact.matchToast", { name: l.name })
          : l.is_request ? t("msg.wantsToContact", { name: l.name }) : t("msg.newFrom", { name: l.name });
        toast(msg, {
          action: { label: t("msg.open"), onClick: () => navigate(`/mensajes/${l.conversation_id}`) },
        });
      }
      if (l && (lastSeen.current === null || l.at > lastSeen.current)) lastSeen.current = l.at;
      if (lastSeen.current === null) lastSeen.current = "";
    } catch {
      /* polling is best-effort */
    }
  }, [user, t, navigate]);

  useEffect(() => {
    lastSeen.current = null;
    if (!user) {
      setUnread(EMPTY);
      return undefined;
    }
    refresh();
    const id = setInterval(refresh, 15000);
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return <UnreadContext.Provider value={{ unread, refresh }}>{children}</UnreadContext.Provider>;
}

export const useUnread = () => useContext(UnreadContext);
